import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { AxiosResponse } from "axios";
import { Formik } from "formik";
import { isEmpty } from "lodash-es";
import moment from "moment";
import { forwardRef, useMemo, useRef, useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import AccessoriesSelectOrCreate from "@/common/components/Entity/Accessory/AccessoriesSelectOrCreate";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { FormikHelper } from "@/common/helpers/formik";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { DurationRepresentation } from "@/common/ts/duration";
import { BaseEntityDefaultValues } from "@/common/ts/entity";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  ContractCreateDto,
  ContractDto,
  ContractStage,
  ContractType,
  ContractUpdateDto,
  EntityType,
  PaginationDtoOfContractDto,
  SpotType,
  UnitOfTime,
  VehicleType,
} from "@/core/api/generated/v1";

import FoldableBlock from "../../Display/FoldableBlock";
import ApiEnumAutocomplete from "../../Enum/ApiEnumAutocomplete";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import FormikComputedField from "../../Form/Formik/FormikComputedField";
import AppTextArea from "../../Form/Input/AppTextArea";
import DurationInput from "../../Form/Input/DurationInput";
import AppLink from "../../Link/AppLink";
import AppTypography from "../../Text/AppTypography";
import AppTooltip from "../../Tooltip/AppTooltip";
import CustomerAutocompleteOrCreate from "../Customer/CustomerAutocompleteOrCreate";
import { CustomerDefaultValues } from "../Customer/CustomerCreateUpdate";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import { SpotDefaultValues } from "../Spot/SpotCreateUpdate";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import { VehicleDefaultValues } from "../Vehicle/VehicleCreateUpdate";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";
import ContractLink from "./ContractLink";
import ContractSettingsInput from "./ContractSettingsInput";
import ContractSpotInfoInput from "./ContractSpotInfoInput";

export type ContractDefaultValues = BaseEntityDefaultValues & {
  vehicleId?: ContractCreateDto["vehicleId"];
  vehicleValues?: VehicleDefaultValues;
  customerId?: ContractCreateDto["customerId"];
  customerValues?: CustomerDefaultValues;
  type?: ContractCreateDto["type"];
  externalNumber?: ContractCreateDto["externalNumber"];
  startsAt?: ContractCreateDto["startsAt"];
  endsAt?: ContractCreateDto["endsAt"];
  notes?: ContractCreateDto["notes"];
  attachments?: ContractCreateDto["attachments"];
  checkOutSpotInfo?: ContractCreateDto["checkOutSpotInfo"] & {
    spotValues?: Nil<SpotDefaultValues>;
  };
  checkInSpotInfo?: ContractCreateDto["checkInSpotInfo"] & { spotValues?: Nil<SpotDefaultValues> };
  extractedDataId?: ContractCreateDto["extractedDataId"];
};

export type ContractCreateUpdateHandle = FormWrapperHandle;

export interface ContractCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  ContractDto,
  ContractDefaultValues
> {
  contractId?: string;
}

export default forwardRef<ContractCreateUpdateHandle, ContractCreateUpdateProps>(
  function ContractCreateUpdate(
    { contractId, defaultValues, onCreate, onUpdate, onSave, onCancel }: ContractCreateUpdateProps,
    ref,
  ) {
    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();
    const userAffiliation = useUserAffiliation();

    const isCreate = !contractId;
    const isEdit = !!contractId;
    const canUpdateVehicle = isCreate;

    const isCheckOutDateEditedRef = useRef(false);
    const isCheckInDateEditedRef = useRef(false);

    const [isAttachmentFilesUploading, setIsAttachmentFilesUploading] = useState(false);

    const contractRequest = useApiRequest(
      apiClient.contractsApi.apiV1ContractsContractIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        contractId: contractId!,
      },
      {
        skip: !contractId,
      },
    );
    const contract = contractRequest?.data;

    const createMetaRequest = useApiRequest(
      apiClient.contractsApi.apiV1ContractsCreateMetaGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      },
      {
        deps: [],
      },
    );
    const createMeta = createMetaRequest?.data;

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: contract && {
        idValue: contract.id!,
        newTitle: contract.localNumber || "",
      },
    });

    const [alreadyExistsRelatedContracts, setAlreadyExistsRelatedContracts] = useState<
      ContractDto[]
    >([]);

    const showOtherContractsFoundAlert = useMemo(() => {
      return alreadyExistsRelatedContracts && alreadyExistsRelatedContracts.length !== 0
        ? alreadyExistsRelatedContracts.filter((x) => x.id !== contractId).length !== 0
        : false;
    }, [alreadyExistsRelatedContracts]);

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.Contract}
        entityId={contractId}
        entity={contract}
        entityRequest={contractRequest}
        isIniting={userAffiliation.isIniting}
      >
        <Formik<
          BaseFormikValues &
            ContractCreateDto &
            ContractUpdateDto & {
              initialAttachments: ContractCreateDto["attachments"];
              isAttachmentFilesHaveErrors: boolean;
            }
        >
          enableReinitialize={true}
          initialValues={{
            externalNumber: defaultValues?.externalNumber || contract?.externalNumber || undefined,
            vehicleId: defaultValues?.vehicleId || contract?.vehicle?.id || undefined,
            accessoryIds: contract?.accessories?.map((x) => x.id!) || [],
            customerId: defaultValues?.customerId || contract?.customer?.id || undefined,
            type: defaultValues?.type || contract?.type || ContractType.Rental,
            startsAt: defaultValues?.startsAt || contract?.startsAt || null,
            endsAt: defaultValues?.endsAt || contract?.endsAt || null,
            bufferBefore: contract?.bufferBefore || null,
            bufferAfter: contract?.bufferAfter || null,
            reservationExpiresAt: contract?.reservationExpiresAt || null,
            departmentIds:
              (!isEmpty(contract?.departmentIds) ? contract?.departmentIds : undefined) ||
              (!isEmpty(userAffiliation.departments)
                ? userAffiliation.departments?.map((d) => d.id!)
                : undefined) ||
              [],
            locationIds:
              (!isEmpty(contract?.locationIds) ? contract?.locationIds : undefined) ||
              (!isEmpty(userAffiliation.locations)
                ? userAffiliation.locations?.map((l) => l.id!)
                : undefined) ||
              [],
            checkOutSpotInfo:
              (defaultValues?.checkOutSpotInfo || contract?.checkOutSpotInfo
                ? {
                    ...contract?.checkOutSpotInfo,
                    date:
                      defaultValues?.checkOutSpotInfo?.date ||
                      contract?.checkOutSpotInfo?.date ||
                      undefined,
                    spotId:
                      defaultValues?.checkOutSpotInfo?.spotId ||
                      contract?.checkOutSpotInfo?.spot?.id ||
                      undefined,
                  }
                : undefined) || undefined,
            checkInSpotInfo:
              (defaultValues?.checkInSpotInfo || contract?.checkInSpotInfo
                ? {
                    ...contract?.checkInSpotInfo,
                    date:
                      defaultValues?.checkInSpotInfo?.date ||
                      contract?.checkInSpotInfo?.date ||
                      undefined,
                    spotId:
                      defaultValues?.checkInSpotInfo?.spotId ||
                      contract?.checkInSpotInfo?.spot?.id ||
                      undefined,
                  }
                : undefined) || undefined,
            notes: defaultValues?.notes || contract?.notes || undefined,
            initialAttachments: defaultValues?.attachments || contract?.attachments || undefined,
            attachments: !isEmpty(defaultValues?.attachments)
              ? defaultValues?.attachments
              : !isEmpty(contract?.attachments)
                ? contract?.attachments
                : undefined,
            isAttachmentFilesHaveErrors: false,
            isConfirmVehicleAllocation: true,
            isReserved: false,
            settings: contract?.settings || createMeta?.settings || undefined,
            tags: contract?.tags || undefined,
            extractedDataId: isCreate ? defaultValues?.extractedDataId : undefined,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // vehicleId: Yup.string().required("Vehicle is required"),
            // customerId: Yup.string().required("Customer is required"),
            // type: Yup.string().required("Type is required"),
          })}
          onSubmit={async (values, { setFieldValue, setFieldError, setStatus, setSubmitting }) => {
            try {
              const values2: typeof values = {
                ...values,
                startsAt: values.startsAt && moment(values.startsAt).utc().format(),
                endsAt: values.endsAt && moment(values.endsAt).utc().format(),
                reservationExpiresAt:
                  values.reservationExpiresAt && moment(values.reservationExpiresAt).utc().format(),
                checkOutSpotInfo: {
                  ...values.checkOutSpotInfo,
                  date:
                    values.checkOutSpotInfo?.date &&
                    moment(values.checkOutSpotInfo?.date).utc().format(),
                },
                checkInSpotInfo: {
                  ...values.checkInSpotInfo,
                  date:
                    values.checkInSpotInfo?.date &&
                    moment(values.checkInSpotInfo?.date).utc().format(),
                },
              };

              if (isCreate) {
                const response = await apiClient.contractsApi.apiV1ContractsPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  contractCreateDto: {
                    ...values2,
                  },
                });
                enqueueSnackbar("Contract created.", { variant: "success" });
                onCreate && onCreate(response.data);
                onSave && onSave(response.data);
              } else {
                const response = await apiClient.contractsApi.apiV1ContractsContractIdPut({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  contractId,
                  contractUpdateDto: {
                    ...values2,
                  },
                });

                enqueueSnackbar("Contract updated.", { variant: "success" });
                onUpdate && onUpdate(response.data);
                onSave && onSave(response.data);
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
            } = formikProps;

            return (
              <>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                {/* Suggest existing contracts for the date range. */}
                <FormikComputedField<
                  any,
                  {
                    alreadyExistsRelatedContracts: ContractDto[];
                  }
                >
                  deps={[values.vehicleId, values.customerId, values.startsAt, values.endsAt]}
                  compute={async (params) => {
                    let response: AxiosResponse<PaginationDtoOfContractDto, any> | undefined =
                      undefined;
                    if (
                      params.values.customerId &&
                      params.values.vehicleId &&
                      (params.values.startsAt || params.values.endsAt)
                    ) {
                      response = await apiClient.contractsApi.apiV1ContractsGetPost({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        contractGetPaginatedDto: {
                          customerId: params.values.customerId || undefined,
                          vehicleId: params.values.vehicleId || undefined,
                          range: {
                            from: params.values.startsAt || undefined,
                            to: params.values.endsAt || undefined,
                          },
                        },
                      });
                    }

                    const items = response?.data?.items || [];
                    return {
                      alreadyExistsRelatedContracts:
                        items?.filter((x) => x.id !== contractId) || [],
                    };
                  }}
                  onComputed={(computed) => {
                    setAlreadyExistsRelatedContracts(computed.alreadyExistsRelatedContracts);
                  }}
                />

                <FormContentBlock gap={1}>
                  {showOtherContractsFoundAlert && (
                    <Alert severity='info'>
                      Already created{" "}
                      {alreadyExistsRelatedContracts.map((x) => (
                        <ContractLink
                          key={x.id}
                          entity={x}
                          inlineProps={{ withIcon: false, withTooltip: true }}
                        />
                      ))}{" "}
                      was found for selected customer or vehicle in selected date range.
                    </Alert>
                  )}

                  {/* Tags */}
                  <FormControl margin='dense' fullWidth>
                    <GeneralAttachedTagsInput
                      value={values.tags}
                      onChange={(newValue) => {
                        setFieldValue("tags", newValue);
                      }}
                    />
                    <FormHelperText error>
                      {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                        isIncludeNested: false,
                      })}
                    </FormHelperText>
                  </FormControl>

                  <Stack
                    sx={{
                      flexDirection: { xxs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <FormControl
                      margin='dense'
                      fullWidth
                      error={Boolean(touched.type && errors.type)}
                    >
                      <ApiEnumAutocomplete
                        type='ContractType'
                        value={values.type}
                        onlyEnumValues={[
                          ContractType.Rental,
                          ContractType.Leasing,
                          ContractType.Subscription,
                        ]}
                        textFieldProps={{
                          label: "Contract type",
                          error: Boolean(errors.type),
                          helperText: errors.type,
                        }}
                        onChange={(newValue) => setFieldValue("type", newValue)}
                      />
                    </FormControl>

                    <FormControl margin='dense' fullWidth>
                      <TextField
                        error={Boolean(errors.externalNumber)}
                        fullWidth
                        helperText={errors.externalNumber}
                        label='External number'
                        name='externalNumber'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type='text'
                        value={values.externalNumber || ""}
                        variant='outlined'
                        slotProps={{
                          htmlInput: {
                            maxLength: 100,
                          },
                        }}
                      />
                    </FormControl>
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { xxs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <FormControl margin='dense' fullWidth>
                      <VehicleAutocompleteOrCreate
                        autocompleteProps={{
                          required: true,
                          disabled: !canUpdateVehicle,
                          entityId: values.vehicleId,
                          isPreload: false,
                          searchFilters: {
                            type: VehicleType.Car,
                          },
                          textFieldProps: {
                            error: Boolean(errors.vehicleId),
                            helperText: errors.vehicleId,
                          },
                          onChange: (newValue) => {
                            setFieldValue("vehicleId", newValue?.id);
                            setFieldValue(
                              "accessoryIds",
                              newValue?.accessories?.map((x) => x.id) || [],
                            );
                          },
                        }}
                        createUpdateProps={{
                          defaultValues: defaultValues?.vehicleValues,
                        }}
                        createFormPlacement='modal'
                        onCreate={(newValue) => {
                          setFieldValue("vehicleId", newValue?.id);
                          setFieldValue(
                            "accessoryIds",
                            newValue?.accessories?.map((x) => x.id) || [],
                          );
                        }}
                      />

                      {isCreate && (
                        <>
                          <Stack direction='row' spacing={1}>
                            <FormControl
                              margin='none'
                              error={Boolean(errors.isConfirmVehicleAllocation)}
                            >
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name='isConfirmVehicleAllocation'
                                    checked={values.isConfirmVehicleAllocation}
                                    onBlur={handleBlur}
                                    onChange={(e) => {
                                      setFieldValue("isConfirmVehicleAllocation", e.target.checked);
                                    }}
                                  />
                                }
                                label={
                                  <AppTooltip
                                    title={`When confirmed you won't be able to reallocate vehicle no more.`}
                                  >
                                    <AppTypography
                                      decoration={{
                                        variant: "helpText",
                                      }}
                                    >
                                      Confirm vehicle allocation
                                    </AppTypography>
                                  </AppTooltip>
                                }
                              />
                              {errors.isConfirmVehicleAllocation && (
                                <FormHelperText error>
                                  {ValidationHelper.getFormikErrorsAsString(
                                    errors.isConfirmVehicleAllocation,
                                  )}
                                </FormHelperText>
                              )}
                            </FormControl>

                            <FormControl margin='none' error={Boolean(errors.isReserved)}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name='isReserved'
                                    checked={values.isReserved ?? undefined}
                                    onBlur={handleBlur}
                                    onChange={(e) => {
                                      setFieldValue("isReserved", e.target.checked);
                                    }}
                                  />
                                }
                                label={
                                  <AppTooltip
                                    title={
                                      <>
                                        If checked, the contract is created in{" "}
                                        <InlineApiEnumValue
                                          type='ContractStage'
                                          value={ContractStage.Reserved}
                                        />{" "}
                                        stage.
                                      </>
                                    }
                                  >
                                    <AppTypography
                                      decoration={{
                                        variant: "helpText",
                                      }}
                                    >
                                      Reserve
                                    </AppTypography>
                                  </AppTooltip>
                                }
                              />
                              {errors.isReserved && (
                                <FormHelperText error>
                                  {ValidationHelper.getFormikErrorsAsString(errors.isReserved)}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Stack>
                        </>
                      )}
                    </FormControl>

                    <FormControl margin='dense' fullWidth>
                      <CustomerAutocompleteOrCreate
                        autocompleteProps={{
                          required: true,
                          entityId: values.customerId,
                          disabled: isEdit,
                          textFieldProps: {
                            error: Boolean(errors.customerId),
                            helperText: errors.customerId,
                          },
                          onChange: (newValue) => setFieldValue("customerId", newValue?.id),
                        }}
                        createUpdateProps={{
                          defaultValues: defaultValues?.customerValues,
                        }}
                        onCreate={(newValue) => setFieldValue("customerId", newValue?.id)}
                      />
                    </FormControl>
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { xxs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <FormControl margin='none' fullWidth>
                      <AppDateTimePicker
                        label='Starts at'
                        value={(values.startsAt && moment(values.startsAt)) || null}
                        format={DATETIME_FORMATS.DISPLAY_DATETIME}
                        onChange={(newValue) => {
                          setFieldValue("startsAt", newValue?.format() || null);
                          if (!isCheckOutDateEditedRef.current) {
                            setFieldValue("checkOutSpotInfo.date", newValue?.format() || null);
                          }
                          if (!newValue) {
                            setFieldValue("bufferBefore", null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            error: Boolean(errors.startsAt),
                            helperText: errors.startsAt,
                          },
                        }}
                      />
                      <FormHelperText>
                        If you leave the start date empty the contract will be considered as
                        ongoing.
                      </FormHelperText>
                    </FormControl>

                    <FormControl margin='none' fullWidth>
                      <AppDateTimePicker
                        label='Ends at'
                        value={(values.endsAt && moment(values.endsAt)) || null}
                        format={DATETIME_FORMATS.DISPLAY_DATETIME}
                        onChange={(newValue) => {
                          setFieldValue("endsAt", newValue?.format() || null);
                          if (!isCheckInDateEditedRef.current) {
                            setFieldValue("checkInSpotInfo.date", newValue?.format() || null);
                          }
                          if (!newValue) {
                            setFieldValue("bufferAfter", null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            error: Boolean(errors.endsAt),
                            helperText: errors.endsAt,
                          },
                        }}
                      />
                      <FormHelperText>
                        If you leave the end date empty the contract will be considered as ongoing.
                      </FormHelperText>
                    </FormControl>
                  </Stack>

                  {(values.startsAt || values.endsAt) && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(2, 1fr)`,
                        columnGap: 2,
                      }}
                    >
                      <FormControl margin='none' fullWidth>
                        <DurationInput
                          error={Boolean(errors.bufferBefore)}
                          helperText={errors.bufferBefore}
                          disabled={!values.startsAt}
                          fullWidth
                          margin='dense'
                          variant='outlined'
                          label='Buffer before'
                          name='bufferBefore'
                          representation={DurationRepresentation.DotNetTimeSpan}
                          unitsOfTime={[UnitOfTime.Day, UnitOfTime.Hour, UnitOfTime.Minute]}
                          defaultUnitOfTime={UnitOfTime.Hour}
                          value={values.bufferBefore}
                          onChange={(newValue) => {
                            setFieldValue("bufferBefore", newValue);
                          }}
                          onBlur={handleBlur}
                        />
                        <FormHelperText>Additional time before start.</FormHelperText>
                      </FormControl>

                      <FormControl margin='none' fullWidth>
                        <DurationInput
                          error={Boolean(errors.bufferAfter)}
                          helperText={errors.bufferAfter}
                          disabled={!values.endsAt}
                          fullWidth
                          margin='dense'
                          variant='outlined'
                          label='Buffer after'
                          name='bufferAfter'
                          representation={DurationRepresentation.DotNetTimeSpan}
                          unitsOfTime={[UnitOfTime.Day, UnitOfTime.Hour, UnitOfTime.Minute]}
                          defaultUnitOfTime={UnitOfTime.Hour}
                          value={values.bufferAfter}
                          onChange={(newValue) => {
                            setFieldValue("bufferAfter", newValue);
                          }}
                          onBlur={handleBlur}
                        />
                        <FormHelperText>Additional time after end.</FormHelperText>
                      </FormControl>
                    </Box>
                  )}

                  {(values.isReserved ||
                    (isEdit && contract?.stageHistory?.stage === ContractStage.Reserved)) && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(2, 1fr)`,
                        columnGap: 2,
                      }}
                    >
                      <FormControl margin='none' fullWidth>
                        <AppDateTimePicker
                          label='Reservation expires at'
                          value={
                            (values.reservationExpiresAt && moment(values.reservationExpiresAt)) ||
                            null
                          }
                          format={DATETIME_FORMATS.DISPLAY_DATETIME}
                          onChange={(newValue) => {
                            setFieldValue("reservationExpiresAt", newValue?.format() || null);
                          }}
                          slotProps={{
                            textField: {
                              error: Boolean(errors.reservationExpiresAt),
                              helperText: errors.reservationExpiresAt,
                            },
                          }}
                        />
                        <FormHelperText>
                          On expiration the stage is automatically updated to{" "}
                          <InlineApiEnumValue
                            sx={{ fontStyle: "italic" }}
                            type='ContractStage'
                            value={ContractStage.ReservationExpired}
                          />
                          .
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  )}

                  {/* Entity affiliation */}
                  <EntityAffiliationInputV2
                    departments={{
                      departmentIds: values.departmentIds,
                      onChange: (departmentIds) => {
                        setFieldValue("departmentIds", departmentIds);
                      },
                      error: errors.departmentIds,
                    }}
                    locations={{
                      locationIds: values.locationIds,
                      onChange: (locationIds) => {
                        setFieldValue("locationIds", locationIds);
                      },
                      error: errors.locationIds,
                    }}
                  />

                  <Stack
                    sx={{
                      flexDirection: { xxs: "column", md: "row" },
                      gap: 1,
                    }}
                  >
                    <FormControl
                      margin='dense'
                      fullWidth
                      error={Boolean(touched.accessoryIds && errors.accessoryIds)}
                    >
                      <AccessoriesSelectOrCreate
                        autocompleteProps={{
                          withCreate: true,
                          disabled: !values.vehicleId,
                          entityIds: values.accessoryIds,
                          entities: undefined,
                          onChange: (newValues) => {
                            setFieldValue("accessoryIds", newValues?.map((x) => x.id!) || []);
                          },
                        }}
                        onCreate={(newValue) => {
                          setFieldValue("accessoryIds", [...values.accessoryIds!, newValue.id]);
                        }}
                      />
                      <FormHelperText>
                        Accessories are loaded from vehicle by default, but you can select any
                        accessories in scope of the contract.
                      </FormHelperText>
                      <FormHelperText>{touched.accessoryIds && errors.accessoryIds}</FormHelperText>
                    </FormControl>

                    <FormControl
                      margin='dense'
                      fullWidth
                      error={Boolean(touched.notes && errors.notes)}
                    >
                      <AppTextArea
                        error={Boolean(touched.notes && errors.notes)}
                        fullWidth
                        helperText={touched.notes && errors.notes}
                        mode='notes'
                        name='notes'
                        onBlur={handleBlur}
                        onChange={handleChange}
                        value={values.notes}
                        variant='outlined'
                      />
                    </FormControl>
                  </Stack>

                  {values?.settings?.isCheckEnabled && (
                    <Stack
                      sx={{
                        flexDirection: { xxs: "column", md: "row" },
                        gap: 1,
                      }}
                    >
                      <FoldableBlock
                        defaultIsFolded={false}
                        trigger={{
                          label: (
                            <Typography component='span' variant='subtitle1'>
                              Check out spot info
                            </Typography>
                          ),
                        }}
                      >
                        <ContractSpotInfoInput
                          contractId={contract?.id}
                          spotInfo={contract?.checkOutSpotInfo}
                          formikProps={FormikHelper.getSubProps(
                            formikProps,
                            "checkOutSpotInfo",
                            (v) => v.checkOutSpotInfo,
                          )}
                          spotType={SpotType.CheckOut}
                          spotDefaultValues={defaultValues?.checkOutSpotInfo?.spotValues}
                          onDateChange={(newValue) => {
                            isCheckOutDateEditedRef.current = true;
                          }}
                        />
                      </FoldableBlock>

                      <FoldableBlock
                        defaultIsFolded={false}
                        trigger={{
                          label: (
                            <Typography component='span' variant='subtitle1'>
                              Check in spot info
                            </Typography>
                          ),
                        }}
                      >
                        <ContractSpotInfoInput
                          contractId={contract?.id}
                          spotInfo={contract?.checkInSpotInfo}
                          formikProps={FormikHelper.getSubProps(
                            formikProps,
                            "checkInSpotInfo",
                            (v) => v.checkInSpotInfo,
                          )}
                          spotType={SpotType.CheckIn}
                          spotDefaultValues={defaultValues?.checkInSpotInfo?.spotValues}
                          onDateChange={(newValue) => {
                            isCheckInDateEditedRef.current = true;
                          }}
                        />
                      </FoldableBlock>
                    </Stack>
                  )}

                  {/* Settings */}
                  <FoldableBlock
                    defaultIsFolded={false}
                    trigger={{
                      label: <Typography variant='h6'>Settings</Typography>,
                    }}
                  >
                    <Box>
                      {isCreate && (
                        <AppTypography sx={{ mb: 1 }} variant='body2'>
                          Using default settings from{" "}
                          <AppLink to={ROUTE_PATH.TENANT_SETTINGS_EDIT}>company settings</AppLink>.
                        </AppTypography>
                      )}

                      <ContractSettingsInput
                        formikProps={FormikHelper.getSubProps(
                          formikProps,
                          "settings",
                          (x) => x.settings,
                        )}
                      />
                    </Box>
                  </FoldableBlock>

                  {/* Other */}
                  <FoldableBlock
                    defaultIsFolded={ArrayHelper.isEmpty(values.initialAttachments)}
                    trigger={{
                      label: (
                        <Typography component='span' variant='h6'>
                          Other
                        </Typography>
                      ),
                    }}
                  >
                    <FormControl fullWidth component='fieldset' margin='dense'>
                      <FileUploader
                        multiple
                        maxFiles={20}
                        defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                        onChange={(newFiles) => {
                          setFieldValue(
                            `attachments`,
                            FileItem.toManyGeneralAttachmentInputDto(newFiles),
                          );
                        }}
                        onUploadStarted={() => {
                          setIsAttachmentFilesUploading(true);
                        }}
                        onUploadFinished={() => {
                          setIsAttachmentFilesUploading(false);
                        }}
                        onValidationStatusChange={(filesValidationStatus) => {
                          if (filesValidationStatus)
                            setFieldValue(
                              "isAttachmentFilesHaveErrors",
                              Object.values(filesValidationStatus).some((x) => x === false),
                            );
                        }}
                      />
                    </FormControl>
                  </FoldableBlock>
                </FormContentBlock>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                <FormActions>
                  <Button
                    onClick={() => handleSubmit()}
                    color='primary'
                    disabled={isAttachmentFilesUploading || values.isAttachmentFilesHaveErrors}
                    loading={isSubmitting}
                    variant='contained'
                  >
                    Save
                  </Button>

                  {onCancel && (
                    <Button
                      variant='outlined'
                      color='text'
                      disabled={isSubmitting}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  )}
                </FormActions>
              </>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
